/**
 * Image Banner Slideshow
 * Handles multi-frame slideshow within a single "Diapositive de semaine" block.
 * Features: Expandable active progress bar (active segment expands in width),
 * clickable progress segments for instant slide navigation,
 * square frameless pause button, configurable paddings.
 */

class ImageBannerSlideshow {
  #banner;
  #frames;
  #segments;
  #progressFills;
  #pauseBtn;
  #currentIndex = 0;
  #totalFrames = 0;
  #duration = 7000;
  #slideDuration = 700;
  #timer = null;
  #isPaused = false;
  #startTime = 0;
  #remainingTime = 0;

  constructor(bannerEl) {
    this.#banner = bannerEl;
    this.#frames = Array.from(bannerEl.querySelectorAll('.banner-slideshow__frame'));
    this.#totalFrames = this.#frames.length;

    // Never keep more progress segments than rendered frames, otherwise clicking
    // an orphan segment would try to navigate to a frame that does not exist
    this.#segments = Array.from(bannerEl.querySelectorAll('.banner-progress__segment'));

    for (const orphan of this.#segments.slice(this.#totalFrames)) {
      orphan.remove();
    }

    this.#segments = this.#segments.slice(0, this.#totalFrames);
    this.#progressFills = Array.from(bannerEl.querySelectorAll('.banner-progress__fill'));
    this.#pauseBtn = bannerEl.querySelector('.banner-pause-btn');

    if (this.#totalFrames <= 1) return;

    const durationAttr = bannerEl.dataset.slideshowDuration;
    this.#duration = durationAttr ? parseInt(durationAttr, 10) * 1000 : 7000;

    const slideDurationAttr = bannerEl.dataset.slideshowSlideDuration;
    this.#slideDuration = slideDurationAttr ? parseInt(slideDurationAttr, 10) : 700;

    this.#banner.style.setProperty('--banner-slide-duration', `${this.#slideDuration}ms`);
    this.#banner.style.setProperty('--banner-slide-total-duration', `${this.#duration}ms`);

    this.#initFrames();
    this.#startProgressBar(0);
    this.#scheduleNext();
    this.#bindPause();
    this.#bindSegments();
  }

  #initFrames() {
    this.#frames.forEach((frame, idx) => {
      frame.classList.toggle('banner-slideshow__frame--active', idx === 0);
      frame.classList.remove('banner-slideshow__frame--out-left');
    });
  }

  #scheduleNext() {
    this.#startTime = Date.now();
    this.#remainingTime = this.#duration;

    this.#timer = setTimeout(() => {
      this.#advance();
    }, this.#duration);
  }

  #advance() {
    const nextIndex = (this.#currentIndex + 1) % this.#totalFrames;
    this.#goTo(nextIndex);
  }

  #goTo(targetIndex) {
    if (targetIndex < 0 || targetIndex >= this.#totalFrames) {
      return;
    }

    if (targetIndex === this.#currentIndex && this.#timer !== null && !this.#isPaused) {
      return;
    }

    clearTimeout(this.#timer);

    const prevIndex = this.#currentIndex;
    this.#currentIndex = targetIndex;

    const prevFrame = this.#frames[prevIndex];
    const nextFrame = this.#frames[this.#currentIndex];

    if (prevIndex !== targetIndex) {
      nextFrame.classList.remove('banner-slideshow__frame--out-left');
      void nextFrame.offsetWidth;

      prevFrame.classList.remove('banner-slideshow__frame--active');
      prevFrame.classList.add('banner-slideshow__frame--out-left');

      nextFrame.classList.add('banner-slideshow__frame--active');

      setTimeout(() => {
        prevFrame.classList.remove('banner-slideshow__frame--out-left');
      }, this.#slideDuration + 50);
    }

    this.#progressFills.forEach((fill, idx) => {
      fill.classList.remove('banner-progress__fill--animating', 'banner-progress__fill--done');
      fill.style.removeProperty('transform');
      if (idx < targetIndex) {
        fill.classList.add('banner-progress__fill--done');
      }
    });

    this.#startProgressBar(targetIndex);

    if (!this.#isPaused) {
      this.#scheduleNext();
    }
  }

  #startProgressBar(index) {
    this.#segments.forEach((seg, idx) => {
      seg.classList.toggle('banner-progress__segment--active', idx === index);
    });

    const fill = this.#progressFills[index];
    if (!fill) return;

    fill.classList.remove('banner-progress__fill--animating', 'banner-progress__fill--done');
    fill.style.removeProperty('transform');
    void fill.offsetWidth;
    fill.classList.add('banner-progress__fill--animating');
  }

  #bindSegments() {
    this.#segments.forEach((seg, idx) => {
      seg.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#goTo(idx);
      });
    });
  }

  #pauseProgressBar() {
    const fill = this.#progressFills[this.#currentIndex];
    if (!fill) return;

    const computedStyle = window.getComputedStyle(fill);
    const matrix = new DOMMatrix(computedStyle.transform);
    const scaleX = matrix.a;
    fill.style.transform = `scaleX(${scaleX})`;
    fill.classList.remove('banner-progress__fill--animating');
  }

  #resumeProgressBar(remaining) {
    const fill = this.#progressFills[this.#currentIndex];
    if (!fill) return;

    fill.style.removeProperty('transform');
    fill.style.setProperty('--banner-slide-total-duration', `${remaining}ms`);
    void fill.offsetWidth;
    fill.classList.add('banner-progress__fill--animating');
  }

  #bindPause() {
    if (!this.#pauseBtn) return;

    this.#pauseBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#isPaused ? this.#resume() : this.#pause();
    });

    this.#updatePauseIcon();
  }

  #pause() {
    if (this.#isPaused) return;
    this.#isPaused = true;
    clearTimeout(this.#timer);
    this.#remainingTime = Math.max(0, this.#duration - (Date.now() - this.#startTime));
    this.#pauseProgressBar();
    this.#updatePauseIcon();
  }

  #resume() {
    if (!this.#isPaused) return;
    this.#isPaused = false;
    this.#startTime = Date.now();
    this.#banner.style.setProperty('--banner-slide-total-duration', `${this.#remainingTime}ms`);
    this.#resumeProgressBar(this.#remainingTime);
    this.#timer = setTimeout(() => {
      this.#advance();
    }, this.#remainingTime);
    this.#updatePauseIcon();
  }

  #updatePauseIcon() {
    if (!this.#pauseBtn) return;
    this.#pauseBtn.setAttribute('aria-label', this.#isPaused ? 'Reprendre' : 'Pause');
    this.#pauseBtn.innerHTML = this.#isPaused
      ? `<svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor" aria-hidden="true"><path d="M5 3l8 5-8 5V3z"/></svg>`
      : `<svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor" aria-hidden="true"><path d="M4 3h2.5v10H4V3zm5.5 0H12v10H9.5V3z"/></svg>`;
  }
}

const initialized = new WeakSet();

const initSlideshows = (root) => {
  for (const banner of root.querySelectorAll('.banner[data-slideshow-frames]')) {
    if (initialized.has(banner)) continue;

    if (banner.querySelectorAll('.banner-slideshow__frame').length > 1) {
      initialized.add(banner);
      new ImageBannerSlideshow(banner);
    }
  }
};

document.addEventListener('DOMContentLoaded', () => initSlideshows(document));

// Sections are re-rendered without a page load in the theme editor
document.addEventListener('shopify:section:load', (event) => initSlideshows(event.target));
